import { Type } from "class-transformer";

import { Company } from "../../models/company.model";
import { SlotsBatch } from "../../models/slots-batch.model";
import { endOfDay } from "../../utilities/date";
import { Negotiation } from "../modules/commercial/models/negotiation.model";
import { Contract } from "../modules/contracts/models/contract.model";

export enum SlotRequestStatus {
  UNKNOWN = 0,
  PENDING = 1,
  EXPIRED = 2,
  ASSIGNED = 3,
  ASSIGNED_PARTIALLY = 4,
  RETIRED = 5,
  REJECTED = 6
};

const StatusLocaleNames = Object.freeze({
  [SlotRequestStatus.UNKNOWN]: 'ERROR',
  [SlotRequestStatus.PENDING]: 'SLOTS.STATUS.PENDING',
  [SlotRequestStatus.EXPIRED]: 'SLOTS.STATUS.EXPIRED',
  [SlotRequestStatus.ASSIGNED]: 'SLOTS.STATUS.ASSIGNED',
  [SlotRequestStatus.ASSIGNED_PARTIALLY]: 'SLOTS.STATUS.ASSIGNED_PARTIALLY',
  [SlotRequestStatus.RETIRED]: 'SLOTS.STATUS.WITHDRAWN',
  [SlotRequestStatus.REJECTED]: 'SLOTS.STATUS.REJECTED',
});

export class RequestStatus {
  public id: SlotRequestStatus;
  public name: string;
  public description: string;

  public get locale_name(): string {
    return StatusLocaleNames[this.id] ?? StatusLocaleNames[SlotRequestStatus.UNKNOWN];
  }

  constructor(data: Partial<RequestStatus> = {}) {
    Object.assign(this, data);
  }
}

export class SlotsRequest {
  constructor(data: Partial<SlotsRequest> = {}) {
    Object.assign(this, data);
  }

  readonly id?: number;
  quantity: number;
  /** Cupos asignados (acumulado por batches aceptados). */
  assigned: number;
  /** Cupos rechazados explícitamente al usar "Rechazar restantes" desde estado parcial. */
  rejected: number;

  /**
   * If there is a related {@link Negotiation} to this Batch.
   */
  @Type(() => Negotiation)
  negotiation: Negotiation;

  @Type(() => Contract)
  contract: Contract;

  @Type(() => Date)
  date: Date;

  @Type(() => Date)
  readonly created_at?: Date;

  /**
   * {@link Company} that request the slots.
   */
  @Type(() => Company)
  readonly company?: Company;

  /**
   * {@link Company} that will receive the slots.
   */
  @Type(() => Company)
  recipient: Company;

  /**
   * Additional instructions or comments defined by the
   * [[SlotsBatch.allocator|Allocator]].
   */
  observations?: string;

  destination?: { id: number; name: string };

  /**
   * Slots assigned for this Request
   */
  @Type(() => SlotsBatch)
  readonly batches?: SlotsBatch[];

  @Type(() => RequestStatus)
  public status: RequestStatus;

  /**
   * Quantity of slots assigned for this {@link SlotsRequest} .
   * @returns The total number of slots assigned in the {@link SlotsRequest}.
   */
  public get assignedQuantity(): number {
    const batchIds = new Set(this.batches.map(b => b.id));
    const batchesFiltered = this.batches.filter(batch =>
      (batch.status.id === 1 || batch.status.id === 2) &&
      !batchIds.has(batch.parent_id) // Solo batches raíz para evitar doble conteo
    );

    let total = 0;
    batchesFiltered.forEach(batch => {
      total += batch.slots_total
    });

    return total;
  }

  /**
   * Percentage of slots assigned for this Request.
   */
  public get assignedPercentage(): number {
    if (this.quantity === 0 || this.assignedQuantity === 0) return 0;
    const percentage = (this.assignedQuantity / this.quantity);
    return percentage;
  }

  /**
   * Total de cupos rechazados para visualización.
   * Cuando `rejected` DB > 0 (flag de que se usó "Rechazar restantes" desde solicitud),
   * se consideran rechazados todos los cupos no asignados ni devueltos, sumando así los
   * rechazados desde cupos y los restantes explícitamente rechazados desde solicitud.
   * Si `rejected` DB = 0, solo se cuentan los batch-rechazados (status 3).
   */
  public get effectiveRejectedCount(): number {
    if ((this.rejected || 0) > 0) {
      return Math.max(0, this.quantity - this.assignedQuantity - this.returnedQuantity);
    }
    return this.rejectedBatchQuantity;
  }

  /**
   * Percentage of slots rejected for this Request.
   * Returns 1 when fully rejected, or the partial rejection ratio.
   */
  public get rejectedPercentage(): number {
    if (this.status?.id === SlotRequestStatus.REJECTED) return 1;
    if (this.quantity === 0) return 0;
    const totalRejected = this.effectiveRejectedCount;
    if (!totalRejected) return 0;
    return Math.min(totalRejected / this.quantity, 1);
  }

  /** True cuando la solicitud fue parcialmente asignada y los cupos restantes fueron rechazados (vía rejectSlotRequest). */
  public get hasRejectedRemaining(): boolean {
    return this.status?.id === SlotRequestStatus.ASSIGNED_PARTIALLY && !!this.rejected;
  }

  /**
   * True cuando hay cupos rechazados, ya sea vía rejectSlotRequest explícito,
   * vía rechazo de batch (status 3) en campo `rejected`, o detectado desde los batches.
   */
  public get hasRejected(): boolean {
    return (this.rejected ?? 0) > 0 || this.rejectedBatchQuantity > 0;
  }

  /** True cuando la solicitud tiene batches devueltos (status 5). */
  public get hasReturnedBatches(): boolean {
    return this.returnedQuantity > 0;
  }

  /** True cuando la solicitud pertenece a "Cupos Devueltos/Rechazados": rechazada totalmente, con rechazados, o con devueltos. */
  public get isRejectedOrReturned(): boolean {
    return this.status?.id === SlotRequestStatus.REJECTED || this.hasRejected || this.hasReturnedBatches;
  }

  /**
   * Cantidad de cupos rechazados via rechazo de batch (batches con status 3).
   * Permite detectar rechazos incluso cuando el campo `rejected` del backend es 0.
   */
  public get rejectedBatchQuantity(): number {
    return this.batches
      ?.filter(b => b.status?.id === 3)
      .reduce((sum, b) => sum + (b.slots_total || 0), 0) ?? 0;
  }

  /** Cantidad de cupos devueltos (batches con status 5 - RETURNED). */
  public get returnedQuantity(): number {
    return this.batches
      ?.filter(b => b.status?.id === 5)
      .reduce((sum, b) => sum + (b.slots_total || 0), 0) ?? 0;
  }

  /** Percentage of slots returned for this Request (0 to 1). */
  public get returnedPercentage(): number {
    if (this.quantity === 0 || this.returnedQuantity === 0) return 0;
    return this.returnedQuantity / this.quantity;
  }

  public get expired(): boolean {
    if (this.status?.id !== SlotRequestStatus.EXPIRED) {
      const dueDate = endOfDay(this.date);
      dueDate.setDate(dueDate.getDate() + 1); // Expira el dia posterior al solicitado

      const today = new Date();

      if (dueDate.getTime() < today.getTime()) {
        return true;
        // this.status = new RequestStatus({
        //   id: SlotRequestStatus.EXPIRED,
        //   name: 'expired'
        // });
      }
    }

    return this.status?.id === SlotRequestStatus.EXPIRED;
  }
}
